import { ONLY_APPROVED_ARTISTS_QUERY } from 'app-constants';
import { RemoveIcon, UploadPicture } from 'assets/Modal';
import {
  DocumentSnapshot,
  Timestamp,
  collection,
  deleteField,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { useLiveData } from 'hooks';
import { DashboardRoutePath } from 'modules/dashboard/navigation';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useHistory, useParams } from 'react-router-dom';
import { STORAGE_URL, firestore, getAllCollections, storage } from 'services';
import { fileToObjectUrl, getVideoMetadata } from 'services/media';
import { createSnapshotAsFile } from 'services/media/createSnapshot';
import { generateStaticAssets } from 'services/media/generateStaticAssets';
import { Button, Input, RejectionModal, SelectForm, Splash } from 'shared/Components';
import { FileSelect } from 'shared/Components/FileSelect';
import { Option } from 'shared/Components/Select';
import { Box, ErrorMessage, IconCartContainer, Image, Text } from 'shared/Typhography';
import { FlexContainer } from 'shared/Typhography/flex-box';
import { LinkButton } from 'shared/Typhography/LinkButton';
import { strings } from 'strings';
import { VideoPuzzleDTO } from 'types';
import { UploadField, uploadVideoSchema } from 'validations/uploadImage';

import { useModalProvider } from '../../../../context/ModalProviderContext';
import { useForm } from '../../../../hooks';
import { Status } from '../../../../shared/enums';
import usePremiumPuzzlePrices from '../CreatePuzzleScreen/hooks/usePremiumPuzzles';

import { formDefaultValues } from './constants/formDefaultValues';
import { PuzzleStatusesApprovedArtistSelectOptions } from './constants/PuzzleStatusesApprovedArtistSelectOptions';
import { PuzzleStatusSelectOptions } from './constants/PuzzleStatusSelectOptions';
import { VideoFileState } from './type/FileStates';
import { FormInputs } from './type/FormInputs';

const getTimestampFromUrl = (url: string) => {
  try {
    return new URL(url).searchParams.get('timestamp');
  } catch (error) {
    // In case of invalid url, return null.
    return null;
  }
};

export const ManageVideoPuzzleScreen = () => {
  const { id: paramId } = useParams<{ id?: string }>();
  const history = useHistory();
  const { openModal, closeModal } = useModalProvider();
  const isCreate = useMemo(() => !paramId, [paramId]);

  const fileRef = useRef<File>();
  const [fileState, setFileState] = useState<VideoFileState>();

  const { data: collectionsData, isLoading: collectionDataLoading } = getAllCollections();
  const [collections, setCollections] = useState<any>([]);

  const [artists, setArtists] = useState<any>([]);
  const { docs: artistsData, state: artistsFetchState } = useLiveData(ONLY_APPROVED_ARTISTS_QUERY);

  const { premiumPuzzlePricesDropdownList, isLoadingPremiumPuzzlePrices } =
    usePremiumPuzzlePrices();

  const defaultDataLoadedRef = useRef<boolean>(false);

  const [isSubmitting, setSubmitting] = useState<boolean>(false);

  const [existingPuzzleData, setExistingPuzzleData] = useState<DocumentSnapshot<VideoPuzzleDTO>>();

  const artistsDataLoading = artistsFetchState === 'loading';

  const originalRelativeUrl = existingPuzzleData?.data()?._meta.original;
  const previousVersionUrl = originalRelativeUrl
    ? `${STORAGE_URL}/${originalRelativeUrl.replace(
        'original',
        'original_previous',
      )}?timestamp=${new Date().getTime()}`
    : '';

  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    reset,
    formState: { errors },
  } = useForm<FormInputs>({
    schema: uploadVideoSchema,
    defaultValues: formDefaultValues,
  });

  const openRejectionModal = useCallback(() => {
    openModal('reject-puzzle-modal-message', RejectionModal as React.FC, {
      rejectionModalType: 'puzzle',
      onClose: () => closeModal('reject-puzzle-modal-message'),
      onSubmit: async (message: string) => {
        setValue('rejectMessage', message as any as never);
        await trigger('status');
      },
      id: paramId,
    });
  }, [paramId]);

  const onFileAddedHandler = useCallback(
    async (file: File) => {
      if (file.type !== 'video/mp4') {
        setFileState({
          state: 'error',
          errorMessage: `File must be one of mp4 type`,
        });

        return;
      }

      setFileState({
        errorMessage: undefined,
        state: 'loading',
      });
      try {
        fileRef.current = file;
        const fileUrl = fileToObjectUrl(file);
        const metadata =
          file.type.split('/')[0] === 'video' ? await getVideoMetadata(fileUrl) : undefined;

        setFileState({
          errorMessage: undefined,
          state: 'added',
          url: fileUrl,
          ...(metadata
            ? { resolution: `${metadata.width}x${metadata.height}`, duration: metadata.duration }
            : {}),
        });
      } catch (err) {
        setFileState({ errorMessage: `${(err as Error)?.message}`, state: 'error' });
      }
    },
    [fileState],
  );

  const getArtistsData = (currentSelectedArtist?: string) => {
    const artistLookup: { [key: string]: Option } = {};
    artistsData?.forEach((item) => {
      const elem = {
        value: item.id,
        label: (
          <FlexContainer gap="1rem" alignItems="center" flexDirection="row">
            <Image
              size="profileImage"
              borderRadiusType="circle"
              src={item.data().profileImageUrl}
            />
            {item.data().Name} ({item.data().username})
          </FlexContainer>
        ),
        searchValue: item.data().Name,
      };
      artistLookup[item.id] = elem;
    });

    if (currentSelectedArtist && artistLookup[currentSelectedArtist]) {
      setValue(UploadField.ArtistId, artistLookup[currentSelectedArtist] as any);
    }
    setArtists(Object.keys(artistLookup).map((key) => artistLookup[key]));
  };

  const getCollections = () => {
    const finalData = collectionsData?.docs
      ?.map((item) => ({
        value: item.data().id,
        label: item.data().name,
        id: item.id,
      }))
      .sort((a, b) => {
        if (a.label.toLowerCase() < b.label.toLowerCase()) return -1;
        if (a.label.toLowerCase() > b.label.toLowerCase()) return 1;
        return 0;
      });
    setCollections(finalData);
  };

  const submit = useCallback(
    async (data: FormInputs) => {
      // Validate that all files are added
      if ((!fileRef.current && fileState?.state !== 'exists') || !fileState?.state) {
        setFileState({
          ...fileState,
          state: 'error',
          errorMessage: 'File is missing',
        });
        return;
      }

      setSubmitting(true);
      const puzzlesCollectionRef = collection(firestore, 'puzzles');
      const puzzleDocRef = isCreate
        ? doc(puzzlesCollectionRef)
        : doc(firestore, `puzzles/${paramId as string}`);
      const id = puzzleDocRef.id;

      try {
        const extraFields: Partial<Omit<VideoPuzzleDTO, '_meta'>> = {};
        const extraMeta: Partial<VideoPuzzleDTO['_meta']> = {};

        if (fileState.state === 'added' && fileRef.current) {
          const snapshotFile = await createSnapshotAsFile(fileRef.current);
          const { iconVersion, previewPlaceholderVersion } = await generateStaticAssets(
            snapshotFile,
          );

          const originalBuffer = await fileRef.current?.arrayBuffer();
          const iconBuffer = await iconVersion.file.arrayBuffer();
          const previewPlaceholderBuffer = await previewPlaceholderVersion.file.arrayBuffer();

          const originalReference = ref(storage, `videoPuzzles/${id}/original`);
          const iconReference = ref(storage, `videoPuzzles/${id}/icon`);
          const previewPlaceholderReference = ref(storage, `videoPuzzles/${id}/previewPlaceholder`);

          const [originalResult, iconResult, previewPlaceholderResult] = await Promise.all([
            uploadBytes(originalReference, originalBuffer, { contentType: 'video/mp4' }),
            uploadBytes(iconReference, iconBuffer, { contentType: 'image/png' }),
            uploadBytes(previewPlaceholderReference, previewPlaceholderBuffer, {
              contentType: 'image/png',
            }),
          ]);

          extraMeta.original = originalResult.ref.fullPath;
          extraMeta.originalResolution = fileState.resolution;

          extraFields.iconURL = `${STORAGE_URL}/${
            iconResult.ref.fullPath
          }?timestamp=${new Date().getTime()}`;
          extraFields.videoPreviewPlaceholderUrl = `${STORAGE_URL}/${
            previewPlaceholderResult.ref.fullPath
          }?timestamp=${new Date().getTime()}`;
        }
        const timestamp = serverTimestamp() as Timestamp;

        const shouldAddPublishedAtOnCreate =
          data.forceSetPublishDate || data.status.value === 'approved';

        const existingPuzzle = existingPuzzleData?.data();

        const shouldAddPublishedAtOnUpdate = data.forceSetPublishDate;

        const newStatus =
          data.status.value === 'approved' && existingPuzzle?.status !== 'approved'
            ? Status.Processing
            : data.status.value;

        if (newStatus === Status.Rejected) {
          extraMeta.previousCollection = existingPuzzleData?.data()?.collections[0];
        }

        const puzzleData: Partial<VideoPuzzleDTO> = (
          isCreate
            ? {
                createdAt: timestamp,
                Publisher: data.Publisher.value,
                name: data.name,
                collections: [data.collection.id, data.secondaryCollection?.id].filter((value) =>
                  Boolean(value),
                ),
                aiGenerated: data?.aiGenerated,
                premiumTier: data?.premiumTier?.value ?? 0,
                keywords: data.keywords.toUpperCase(),
                type: 'video',
                status: newStatus as VideoPuzzleDTO['status'],
                plays: 0,
                likes: 0,
                _meta: {
                  original: `videoPuzzles/${id}/original`,
                  ...extraMeta,
                },
                ...(shouldAddPublishedAtOnCreate
                  ? {
                      publishedAt: timestamp,
                    }
                  : {}),
                ...extraFields,
              }
            : {
                Publisher: data.Publisher.value,
                name: data.name,
                collections: [data.collection.id, data.secondaryCollection?.id].filter((value) =>
                  Boolean(value),
                ),
                status: newStatus as VideoPuzzleDTO['status'],
                aiGenerated: data?.aiGenerated,
                premiumTier: data?.premiumTier?.value ?? 0,
                keywords: data.keywords.toUpperCase(),
                rejectMessage: newStatus === 'rejected' ? data.rejectMessage : deleteField(),
                ...(shouldAddPublishedAtOnUpdate
                  ? {
                      publishedAt: timestamp,
                    }
                  : {}),
                ...extraFields,
                ...(0 < Object.keys(extraMeta).length ? { _meta: extraMeta } : {}),
              }
        ) as Partial<VideoPuzzleDTO>;

        await setDoc(puzzleDocRef, puzzleData, { merge: true });

        setFileState(undefined);
        fileRef.current = undefined;

        isCreate ? reset() : history.push(`/${DashboardRoutePath.PUZZLES}`);
      } catch (err) {
        toast.error('Can not save puzzle data. Try again later');
      }
      setSubmitting(false);
    },
    [fileState, existingPuzzleData],
  );

  const fetchCurrentDocData = useCallback(async () => {
    if (defaultDataLoadedRef.current || isCreate) return;

    const puzzleDocRef = doc(firestore, `puzzles/${paramId as string}`);
    const puzzleDoc = (await getDoc(puzzleDocRef)) as DocumentSnapshot<VideoPuzzleDTO>;
    const puzzleData = puzzleDoc.data();

    setValue(UploadField.Name, puzzleData?.name as string);

    const premiumTierDefault = premiumPuzzlePricesDropdownList.find(
      (item: any) => item?.value === (puzzleData?.premiumTier as number),
    );

    setValue(UploadField.PremiumTier, premiumTierDefault);
    setValue(UploadField.AIGenerated, !!puzzleData?.aiGenerated);
    getArtistsData(puzzleData?.Publisher);

    const primaryCollectionId = puzzleData?.collections?.[0] as string;
    const secondaryCollectionId = puzzleData?.collections?.[1];

    const collectionDefault = collections.find((item: any) => item.id === primaryCollectionId);
    const secondaryCollectionDefault = collections.find(
      (item: any) => item.id === secondaryCollectionId,
    );

    setValue(UploadField.Collection, collectionDefault);
    if (secondaryCollectionId) {
      setValue(UploadField.SecondaryCollection, secondaryCollectionDefault);
    }

    setValue(UploadField.Keywords, puzzleData?.keywords as string);

    const currentStatus = PuzzleStatusSelectOptions.find(
      (status) => status.value === puzzleData?.status,
    ) || { value: puzzleData?.status || '', label: puzzleData?.status };
    if (currentStatus) {
      setValue(UploadField.Status, currentStatus);
    }

    // Extract time when image was last updated from icon url's timestamp search param.
    // This allows us to break cache and fetch latest image without being too aggressive.
    // If iconUrl isn't present, or is somehow invalid, fallback to using current timestamp.
    const iconUrl = puzzleDoc?.data()?.iconURL;
    const lastImageUpdateTimestamp = iconUrl
      ? getTimestampFromUrl(iconUrl)
      : new Date().getTime().toString();

    setFileState({
      state: 'exists',
      url: `${STORAGE_URL}/${
        puzzleDoc?.data()?._meta?.original
      }?timestamp=${lastImageUpdateTimestamp}`,
    });

    // if(puzzle.type === "video") {
    //   await generateVideoAssets(id, puzzle?._meta?.original);
    // }

    defaultDataLoadedRef.current = true;
    setExistingPuzzleData(puzzleDoc);
  }, [premiumPuzzlePricesDropdownList, collections, isCreate]);

  useEffect(() => {
    getArtistsData();
  }, [artistsData]);

  useEffect(() => {
    getCollections();
  }, [collectionsData]);

  useEffect(() => {
    if (!isCreate && collections?.length > 0 && premiumPuzzlePricesDropdownList?.length > 0) {
      fetchCurrentDocData();
    }
  }, [collections, premiumPuzzlePricesDropdownList, isCreate]);

  const submitDisabled =
    collectionDataLoading || isLoadingPremiumPuzzlePrices || artistsDataLoading || isSubmitting;

  return (
    <FlexContainer
      margin={{ marginTop: '150px' }}
      dimensions={{ width: '100%' }}
      justifyContent="center"
    >
      <Box width="80%">
        <FlexContainer flexDirection="column" dimensions={{ width: '100%' }} gap="20px">
          <Text size={2} weight="bold">
            {isCreate ? 'Create' : 'Update'} Video puzzle
          </Text>
          <form onSubmit={handleSubmit(submit)}>
            <FlexContainer
              flexDirection="row"
              flexWrap="wrap"
              gap="20px"
              justifyContent="center"
              alignContent="center"
            >
              <FlexContainer
                flexDirection="column"
                dimensions={{ width: '30%', minWidth: '300px' }}
                gap="10px"
                justifyContent="center"
                alignItems="center"
              >
                <Text size={1.5} weight="bold" textAlign="center">
                  Original
                </Text>
                {fileState?.state && ['added', 'exists'].includes(fileState?.state) && (
                  <FlexContainer flexDirection="column" gap="8px">
                    <a href={fileState?.url} target="_blank" rel="noreferrer">
                      <FlexContainer
                        position="relative"
                        dimensions={{ width: '100%', height: '100%' }}
                      >
                        <video
                          style={{ width: '100%', height: '100%' }}
                          loop={true}
                          autoPlay={true}
                          muted={true}
                        >
                          <source src={fileState?.url} type="video/mp4" />
                        </video>

                        <FlexContainer
                          position="absolute"
                          dimensions={{ width: '100%', height: '100%' }}
                          justifyContent="flex-end"
                          alignItems="flex-start"
                          padding={{ padding: '32px' }}
                          style={{ zIndex: 1 }}
                        >
                          <IconCartContainer
                            onClick={(event) => {
                              event.preventDefault();
                              setFileState(undefined);
                              delete fileRef.current;
                              fileRef.current = undefined;
                            }}
                          >
                            <RemoveIcon />
                          </IconCartContainer>
                        </FlexContainer>
                      </FlexContainer>
                    </a>

                    {existingPuzzleData?.get('status') === Status.Edited && (
                      <LinkButton href={previousVersionUrl} target="_blank" rel="noreferrer">
                        Previous Version
                      </LinkButton>
                    )}
                  </FlexContainer>
                )}
                {!(fileState?.state && ['added', 'exists'].includes(fileState?.state)) && (
                  <FileSelect
                    accept="video/mp4"
                    onChange={(event) =>
                      event.currentTarget.files &&
                      event.currentTarget.files.length > 0 &&
                      onFileAddedHandler(event.currentTarget.files[0])
                    }
                    styling={{
                      dimensions: { flex: '1', width: '100%', minHeight: '300px' },
                      gap: '10px',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: { padding: '16px' },
                    }}
                  >
                    <img src={UploadPicture} alt="Upload Image" />
                    <Text color="gray" textAlign="center">
                      Click here or drag and drop file
                    </Text>
                    <Text color="gray" textAlign="center">
                      Make sure your file is <b>mp4</b>
                    </Text>

                    {fileState?.state === 'error' && (
                      <ErrorMessage style={{ position: 'relative', left: 0, bottom: 0 }}>
                        {fileState?.errorMessage}
                      </ErrorMessage>
                    )}
                    {fileState?.resolution && (
                      <Text color="gray" textAlign="center" weight="bold">
                        {`${fileState?.resolution} px`}
                      </Text>
                    )}
                  </FileSelect>
                )}
              </FlexContainer>

              <FlexContainer
                dimensions={{ width: '100%' }}
                gap="50px"
                alignItems="center"
                flexDirection="column"
                margin={{ marginTop: '50px' }}
              >
                <FlexContainer
                  dimensions={{ width: '100%' }}
                  flexDirection="row"
                  gap="50px"
                  flexWrap="wrap"
                >
                  <FlexContainer
                    dimensions={{ flex: 1, minWidth: '300px', width: '100%' }}
                    flexDirection="column"
                    gap="50px"
                    alignItems="center"
                  >
                    <Input
                      label="Name"
                      required
                      control={control}
                      type="text"
                      width="100%"
                      outerWidth="100%"
                      name={UploadField.Name}
                      error={errors[UploadField.Name]}
                      minWidth="200px"
                      placeholder={`Enter ${UploadField.Name}`}
                    />
                    <SelectForm
                      required
                      width="100%"
                      placeholder={strings.selectArtist}
                      values={artists}
                      control={control}
                      name={UploadField.ArtistId}
                      error={errors[UploadField.ArtistId]}
                      label="Artist"
                      id="artist-select-dropdown"
                      includeCustomSearch
                    />
                    <SelectForm
                      required
                      width="100%"
                      placeholder={strings.selectPuzzleTier}
                      values={premiumPuzzlePricesDropdownList}
                      control={control}
                      name={UploadField.PremiumTier}
                      label="Price"
                      id="tier-select-dropdown"
                      margin="33px 0 0 5px"
                    />
                    <SelectForm
                      required
                      id="status"
                      label="Status"
                      name={UploadField.Status}
                      placeholder="Select status"
                      onChange={async (option) => {
                        await trigger('status');

                        option?.value === 'rejected' && !isCreate && openRejectionModal();
                      }}
                      control={control}
                      width="100%"
                      values={PuzzleStatusesApprovedArtistSelectOptions}
                      error={errors[UploadField.Status]}
                    />
                  </FlexContainer>
                  <FlexContainer
                    dimensions={{ flex: 1, minWidth: '300px', width: '100%' }}
                    flexDirection="column"
                    gap="50px"
                    alignItems="center"
                  >
                    <FlexContainer
                      gap="10px"
                      alignItems="center"
                      justifyContent="center"
                      dimensions={{ width: '50%', height: '100%' }}
                      flexDirection="column"
                    >
                      <Input
                        control={control}
                        type="checkbox"
                        width="25px"
                        defaultChecked={false}
                        defaultValue={false}
                        name={UploadField.AIGenerated}
                        placeholder={strings.aiGenerated}
                        outerWidth="50%"
                        maxOuterWidth="25px"
                      />
                      <Text size={0.8} textAlign="left">
                        AI Generated
                      </Text>
                    </FlexContainer>
                    <FlexContainer
                      flexDirection="row"
                      justifyContent="space-evenly"
                      alignItems="center"
                      gap="1rem"
                    >
                      <Input
                        control={control}
                        type="checkbox"
                        width="25px"
                        minWidth="25px"
                        name={UploadField.ForceSetPublishDate}
                        defaultValue={false}
                      />
                      <Text size={0.8}>Force set publish date</Text>
                    </FlexContainer>
                    <SelectForm
                      width="100%"
                      required
                      placeholder={strings.selectCollection}
                      values={collections}
                      control={control}
                      name={UploadField.Collection}
                      error={errors[UploadField.Collection]}
                      label="Primary Category"
                      id="primary-collection-select-dropdown"
                    />
                    <SelectForm
                      width="100%"
                      required
                      placeholder={strings.selectCollection}
                      values={collections}
                      control={control}
                      name={UploadField.SecondaryCollection}
                      isClearable={true}
                      label="Secondary Category"
                      id="secondary-collection-select-dropdown"
                    />
                  </FlexContainer>
                </FlexContainer>
                <Input
                  label="Keywords"
                  required
                  control={control}
                  type="textarea"
                  outerWidth="100%"
                  height="120px"
                  placeholder={UploadField.Keywords}
                  name={UploadField.Keywords}
                  error={errors[UploadField.Keywords]}
                />
              </FlexContainer>

              <FlexContainer
                flexDirection="row"
                justifyContent="center"
                gap="30px"
                margin={{ margin: '20px 0 0 0' }}
                dimensions={{ width: '100%' }}
              >
                <Button
                  variant="link"
                  onClick={() => reset()}
                  height={40}
                  width={140}
                  margin="10px"
                >
                  {strings.discard}
                </Button>
                <Button
                  disabled={submitDisabled}
                  height={40}
                  width={140}
                  type="submit"
                  margin="10px"
                >
                  {!submitDisabled ? (
                    isCreate ? (
                      strings.uploadImage
                    ) : (
                      strings.submit
                    )
                  ) : (
                    <Splash color="white" height={25} width={25} />
                  )}
                </Button>
              </FlexContainer>
            </FlexContainer>
          </form>
        </FlexContainer>
      </Box>
    </FlexContainer>
  );
};
